import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersApi, UpdateProfileData } from '@/api/users';
import { UserDetails } from '@/types/api';
import { toast } from 'sonner';

// Query keys
export const userKeys = {
  all: ['users'] as const,
  me: () => [...userKeys.all, 'me'] as const,
};

// Hook to get current user details
export const useUserDetails = () => {
  return useQuery({
    queryKey: userKeys.me(),
    queryFn: usersApi.getMe,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error) => {
      // Don't retry on 401 (unauthorized) or 404 (user not found)
      if ((error as { response?: { status?: number } })?.response?.status === 401 || 
          (error as { response?: { status?: number } })?.response?.status === 404) {
        return false;
      }
      return failureCount < 3;
    },
  });
};

// Hook to refetch user details
export const useRefreshUserDetails = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: usersApi.getMe,
    onSuccess: (data) => {
      queryClient.setQueryData(userKeys.me(), data);
    },
    onError: (error) => {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to fetch user details';
      toast.error(errorMessage);
    },
  });
};

// Hook to update user profile
export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: UpdateProfileData) => usersApi.updateProfile(data),
    onSuccess: (data) => {
      // Update the cached user data
      queryClient.setQueryData(userKeys.me(), data);
    },
    onError: (error) => {
      const errorMessage = (error as { response?: { data?: { error?: string } } })?.response?.data?.error || 'Failed to update profile';
      toast.error(errorMessage);
    },
  });
};